import React, { useState, useEffect, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@telus-uds/components-web';

import Select from './fields/fieldSelect';
import Input from './fields/fieldInput';
import ModalCustom from '../design/shared/modal';
import { Error } from '../design/elements';
import { ModalFormWrapper } from './css';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const ModalForm = ({ fields, open, header, onClose }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    modalForm: { fieldRequiredMessage },
    formButtons: { saveButtonLabel }
  } = localizedStrings;
  const [modalFormFields, setModalFormFields] = useState();
  const [formErrors, setFormErrors] = useState({});

  const setDefaultFields = () => {
    const result = {};

    fields.forEach(item => {
      const {
        fields: { name, value, required }
      } = item;
      const ref = createRef();
      result[name] = { ref, value, required };
    });

    setModalFormFields(result);
  };

  useEffect(() => {
    setDefaultFields();
  }, []);

  useEffect(() => {
    if (open) {
      setDefaultFields();
    }
  }, [open]);

  const getErrors = () => {
    const result = { ...formErrors };

    fields.forEach(item => {
      const {
        fields: { ref, name, required }
      } = item;

      if (required && !modalFormFields[name].value) {
        result[name] = { text: fieldRequiredMessage, ref };
      } else {
        delete result[name];
      }
    });

    return result;
  };

  const onChange = e => {
    const {
      target: { name, value }
    } = e;
    const newFields = { ...modalFormFields };
    const data = { value };

    newFields[name] = { ...newFields[name], ...data };
    setModalFormFields(newFields);
  };

  const onSubmit = () => {
    const errors = getErrors();

    if (typeof errors === 'undefined') return;

    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      onClose();
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  return (
    <ModalCustom open={open} header={header} setOpen={onClose}>
      <ModalFormWrapper>
        {fields.map(item => {
          const {
            fields: { type, name }
          } = item;
          const data = {
            ...item.fields,
            ref:
              modalFormFields &&
              modalFormFields.name &&
              modalFormFields[name].ref,
            onChange
          };
          let result;
          let error;

          if (
            formErrors &&
            Object.prototype.hasOwnProperty.call(formErrors, name)
          ) {
            error = formErrors[name];
          }

          switch (type) {
            case 'Text':
            case 'Number':
              result = <Input data={data} />;
              break;
            case 'Select':
              result = <Select data={data} />;
              break;
            default:
              result = null;
          }

          return (
            <Fragment key={name}>
              {result}
              {error && <Error text={error.text} />}
            </Fragment>
          );
        })}
        <Button
          type="button"
          onClick={onSubmit}
          variant={{ priority: 'high' }}
        >
          {saveButtonLabel}
        </Button>
      </ModalFormWrapper>
    </ModalCustom>
  );
};

ModalForm.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  open: PropTypes.bool.isRequired,
  header: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ModalForm;
